import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wand2, Loader2 } from "lucide-react";
import { REPORTS } from "@/constants/testIds";

export default function GenerateReportPage() {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [month, setMonth] = useState("");
  const [reportType, setReportType] = useState("reactivation");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !teamId) return toast.error("All fields required");
    if (!/^\d{4}-\d{2}$/.test(month)) return toast.error("Month must be YYYY-MM");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("team_id", teamId);
      fd.append("month", month);
      fd.append("report_type", reportType);
      const r = await api.post("/reports/generate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Report now has ${r.data.row_count} rows`);
      nav(`/reports/${r.data.id}`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Daily / monthly data upload</h1>
        <p className="text-sm text-slate-600 mt-1">
          Upload the calling list (columns: <span className="font-mono text-xs">Name, Date, BrandId</span>) for a team. Deposits are counted only up to <b>end of the selected month</b>. Uploading again for the same team+month <b>appends</b> new rows and re-computes existing ones. Uses the primary DB connection.
        </p>
      </div>

      <form onSubmit={submit} className="glass-card p-6 max-w-3xl space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Team</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger className="glass-input mt-1" data-testid={REPORTS.generateTeamSelect}>
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Month</Label>
            <Input placeholder="2026-01" className="glass-input mt-1" data-testid={REPORTS.generateMonthInput}
              value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div>
            <Label>Report type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="glass-input mt-1" data-testid={REPORTS.generateReportType}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reactivation">Reactivation</SelectItem>
                <SelectItem value="ftd">FTD</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div
          className="glass-solid border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
        >
          <Wand2 className="w-8 h-8 mx-auto text-slate-500 mb-2" />
          <div className="text-sm text-slate-700">
            {file ? <span className="font-medium">{file.name}</span> : "Drop input Excel (Name, Date, BrandId)"}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            data-testid={REPORTS.generateFileInput}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <Button type="submit" disabled={busy} className="btn-tactile" data-testid={REPORTS.generateSubmit}
          style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running against MySQL…</> : "Generate report"}
        </Button>
      </form>
    </div>
  );
}
